// source-folder.ts — CTSK-501 (folder capture, "Stage 2b" of CTSK-497).
//
// The relative folder a file came from on a directory drop or folder pick,
// root folder name included ("Taxes 2025/Receipts/Q3"). The web client sends
// it as the `sourceFolder` multipart field of POST /documents/upload; the API
// stamps it on documents.source_folder (migration 0069) while document
// grouping is on for the org.
//
// It is a LABEL inside one org and nothing more: never a storage path, file
// name, authorization input, audit payload or log line. The folder part only,
// never the file name, never an absolute path.
//
// ONE normaliser, used by the upload route, the list filter and the web
// walker, so the rule cannot drift. The server is the authority; the client
// calls it as a courtesy. It never throws: a rejected value is `null`, and
// callers treat null as "no folder" (the upload still succeeds).
//
// Pipeline (order matters):
//   1. non-string                      -> null
//   2. NFC                             (composed === decomposed; names are
//                                       compared as stored, CASE-SENSITIVE)
//   3. backslash -> '/'                (Windows separators)
//   4. split on '/'; drop empty and '.' segments (this also strips a leading
//      '/' and collapses '//')
//   5. any '..' segment                -> null (whole value rejected)
//   6. control / bidi / zero-width character anywhere -> null (the exact set
//      document-group-label.ts uses; imported, not copied)
//   7. caps, counted in characters (code points): 255 per segment, 32 levels,
//      1024 in total (joined) -> null when exceeded
//   8. no segments left                -> null
//   9. segments joined with '/'
//
// Matching is by exact string (or `folder/` prefix), never lower-cased: this
// deliberately differs from group_name, which matches on lower().
import { FORBIDDEN_LABEL_CHARS } from './document-group-label.js';
/** Name of the multipart field on POST /documents/upload. */
export const SOURCE_FOLDER_FIELD = 'sourceFolder';
export const SOURCE_FOLDER_MAX_SEGMENT_LENGTH = 255;
export const SOURCE_FOLDER_MAX_DEPTH = 32;
export const SOURCE_FOLDER_MAX_LENGTH = 1024;
function charLength(s) {
    return Array.from(s).length;
}
/**
 * The sanitised folder path, or `null` when absent, not a string, empty after
 * cleaning, or rejected (`..`, forbidden characters, over a cap).
 */
export function normalizeSourceFolder(value) {
    if (typeof value !== 'string')
        return null;
    const segments = value
        .normalize('NFC')
        .replace(/\\/g, '/')
        .split('/')
        .filter((seg) => seg !== '' && seg !== '.');
    if (segments.length === 0)
        return null;
    if (segments.length > SOURCE_FOLDER_MAX_DEPTH)
        return null;
    for (const seg of segments) {
        if (seg === '..')
            return null;
        if (FORBIDDEN_LABEL_CHARS.test(seg))
            return null;
        if (charLength(seg) > SOURCE_FOLDER_MAX_SEGMENT_LENGTH)
            return null;
    }
    const joined = segments.join('/');
    if (charLength(joined) > SOURCE_FOLDER_MAX_LENGTH)
        return null;
    return joined;
}
/**
 * Escape a folder path for use inside a SQL LIKE pattern (ESCAPE '\\'):
 * `\`, `%` and `_` are literal characters in folder names ("100%_done").
 */
export function escapeLikePattern(value) {
    return value.replace(/[\\%_]/g, (c) => `\\${c}`);
}
//# sourceMappingURL=source-folder.js.map